import React, { RefObject, useCallback, useState } from 'react';
import DeletableItemForm from '../../../../components/forms/DeletableItem/DeletableItemForm';
import { asDraggableContentListItem, useSortableListItem } from '../../SortableContentList';
import { focusNonFocusableElementAndToggleClassName } from '../../../../lib/DomUtils/utils';
import { sendLiveRegionMessage } from '../../../../components/atoms/LiveRegionGlobal/LiveRegionGlobal';
import ModalDialog from '../../../../components/organisms/ModalDialog/ModalDialog';
import { ContextMenuButtonProps } from '../../ContextMenu/partials/ContextMenuButton';
import { getDisplayNameByFileItem, IFileItem } from '../../../../types/api/FileItem';
import { Download } from '../../Download';
import { useSonTranslation } from '../../../../translation/useSonTranslation';
import { PendingDownload } from './PendingDownload';
import { AttachedFilesUploadEditForm } from '../../../../_blocks/AttachedFiles/forms/AttachedFilesUploadEditForm';
import { ContainerWithCommonContextMenu } from '../../ContextMenu';
import { FormResponseErrorType } from '../../../../components/molecules/Form';
import Utils from '../../../../components/molecules/Form/Utils';

function EditableAttachedFileListItem({
  headerRef,
  ...attachment
}: IFileItem & { headerRef?: RefObject<HTMLHeadingElement | null> }) {
  const { t } = useSonTranslation('AttachedFiles');
  const [view, setView] = useState<any>();
  const setViewDefault = useCallback(() => setView(undefined), []);
  const { move, nextItemId, prevItemId } = useSortableListItem(attachment.id);
  const { forms = {} } = attachment;
  const setModalView = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>): void => {
      setView(event.currentTarget.name);
    },
    [setView],
  );
  const moveElementUp = useCallback(() => {
    if (prevItemId) {
      move(prevItemId);
    }
  }, [move, prevItemId]);

  const moveElementDown = useCallback(() => {
    if (nextItemId) {
      move(nextItemId);
    }
  }, [move, nextItemId]);

  const contextMenuActions: ContextMenuButtonProps[] = [
    ...Object.keys(forms).map(viewName => ({
      // @ts-expect-error no move form in this here
      children: t(`form.${viewName}.prompt`),
      key: viewName,
      name: viewName,
      'aria-haspopup': true,
      onClick: setModalView,
    })),
    {
      children: t('moveElementUp'),
      key: 'moveElementUp',
      disabled: !prevItemId,
      onClick: moveElementUp,
    },
    {
      children: t('moveElementDown'),
      key: 'moveElementDown',
      disabled: !nextItemId,
      onClick: moveElementDown,
    },
  ];

  if (view === 'delete') {
    return attachment.forms?.delete
      ? (
          <DeletableItemForm
            id="attached-file-delete-form"
            ariaLabel={t('form.delete.question')}
            cancelAction={setViewDefault}
            model={attachment.forms.delete}
            onSuccess={() => {
              if (headerRef?.current) {
                focusNonFocusableElementAndToggleClassName(
                  headerRef.current,
                  headerRef.current,
                  'block-interactions--focus-on-headline',
                );
              }
              sendLiveRegionMessage(t('form.delete.success'));
            }}
            customErrorMessages={
              {
                [FormResponseErrorType.INVALID_VALUE]: Utils.createStatusMessage(t('form.delete.invalidValue')),
              }
            }
          />
        )
      : null;
  }

  return (
    <>
      <ContainerWithCommonContextMenu
        toolTip={t('manageMenuToolTip', { title: getDisplayNameByFileItem(attachment) })}
        actions={contextMenuActions}
      >
        {attachment.fileState === 'ready' ? <Download {...attachment} /> : <PendingDownload {...attachment} />}
      </ContainerWithCommonContextMenu>
      {view === 'edit' && attachment.forms && attachment.forms.edit && (
        <ModalDialog closeModal={setViewDefault} headline={t('form.edit.prompt')} initialFocusIndex={1}>
          <AttachedFilesUploadEditForm form={attachment.forms.edit} onSuccess={setViewDefault} />
        </ModalDialog>
      )}
    </>
  );
}

export const DraggableAttachedFileListItem = asDraggableContentListItem(EditableAttachedFileListItem);
